'use client'

import * as React from 'react'
import Image from 'next/image'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import type { IndustryCreative } from '@/lib/copy/industry-creatives'

const STEPS = [
  { label: 'Raw UGC', sub: 'Real customer photo pulled in' },
  { label: 'Tag products', sub: 'AI pins each item in the shot' },
  { label: 'Tap a pin', sub: 'Shopper taps any numbered pin' },
  { label: 'Product card', sub: 'Details appear inline — no new page' },
  { label: 'Add to cart', sub: 'One tap, done' },
]
const STEP_MS = 1500

type LayoutDir = 'reel' | 'pdp'

export function IndustryShoppableCreative({
  creative,
  layoutDir,
  hideToggle,
}: {
  creative: IndustryCreative
  layoutDir?: LayoutDir
  hideToggle?: boolean
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })
  const reduce = useReducedMotion()

  const [internalLayout, setInternalLayout] = React.useState<LayoutDir>('reel')
  const layout = layoutDir ?? internalLayout
  const setLayout = (v: LayoutDir) => { if (!layoutDir) setInternalLayout(v) }
  const [playKey, setPlayKey] = React.useState(0)
  const [activeStep, setActiveStep] = React.useState(reduce ? 4 : 0)
  const [done, setDone] = React.useState(!!reduce)

  React.useEffect(() => {
    if (!inView || layout !== 'reel') return
    if (reduce) { setActiveStep(4); setDone(true); return }
    setActiveStep(0); setDone(false)
    let step = 0
    const id = setInterval(() => {
      step += 1
      if (step >= STEPS.length) { setDone(true); clearInterval(id); return }
      setActiveStep(step)
    }, STEP_MS)
    return () => clearInterval(id)
  }, [inView, reduce, playKey, layout])

  const allPins = creative.pins.slice(0, 3)
  const firstPin = creative.pins[0]
  const industryIndex = creative.no

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className="overflow-hidden rounded-3xl border bg-card">

        {/* Layout direction toggle — hidden when carousel controls it */}
        {!hideToggle && (
          <div className="flex items-center gap-3 border-b px-6 py-3">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Layout direction</span>
            <div className="flex rounded-full border bg-secondary p-[3px]">
              {([['reel', 'Reel · Social'], ['pdp', 'On-site · PDP']] as [LayoutDir, string][]).map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setLayout(val)}
                  className={[
                    'rounded-full px-4 py-1.5 text-xs font-semibold transition duration-200',
                    layout === val
                      ? 'bg-foreground text-background shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  ].join(' ')}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {layout === 'reel' ? (
          /* ── REEL / SOCIAL VIEW: animated phone ── */
          <div className="grid gap-0 lg:grid-cols-2">
            {/* Left: info panel */}
            <div className="flex flex-col gap-5 border-b p-6 lg:border-b-0 lg:border-r lg:p-8">
              <div className="flex items-start gap-3">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-[#0F1419]"
                  style={{ background: 'hsl(var(--brand-lime))' }}
                >
                  {industryIndex}
                </span>
                <div>
                  <h3 className="font-display text-xl font-semibold">{creative.name}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{creative.tagline}</p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Shot list</p>
                  <p className="text-sm leading-relaxed text-foreground">{creative.shotList}</p>
                </div>
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Tag these</p>
                  <div className="flex flex-wrap gap-1.5">
                    {creative.tags.map((t) => (
                      <span key={t} className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium">{t}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Idukki widget</p>
                  <p className="text-sm font-medium">{creative.widget}</p>
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Embeds on</p>
                  <div className="flex flex-wrap gap-1.5">
                    {creative.embeds.map((e) => (
                      <span key={e} className="rounded-full bg-secondary px-2 py-0.5 text-[11px]">{e}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-auto grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl bg-foreground px-4 py-3 text-center">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-background/50">CTA copy</p>
                  <p className="mt-1 text-base font-bold text-background">&ldquo;{creative.cta}&rdquo;</p>
                </div>
                <div className="rounded-xl border-2 border-emerald-400/40 bg-emerald-500/10 px-4 py-3 text-center">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-400">Expected lift</p>
                  <p className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">{creative.liftNum}</p>
                  <p className="text-xs text-emerald-700/70 dark:text-emerald-400/70">{creative.liftLabel}</p>
                </div>
              </div>
            </div>

            {/* Right: animated phone + steps */}
            <div className="flex flex-col items-center justify-center gap-8 p-6 lg:flex-row lg:items-start lg:p-8">
              {/* Phone */}
              <div className="relative shrink-0" style={{ width: 220, height: 424 }}>
                <div className="absolute inset-0 rounded-[38px]" style={{ background: '#0F1419', boxShadow: '0 28px 72px rgba(0,0,0,0.4)' }} />
                <div className="absolute inset-[10px] overflow-hidden rounded-[28px] bg-muted">
                  <Image src={creative.hero} alt={`${creative.name} UGC`} fill className="object-cover" sizes="200px" />
                  {allPins.map((pin, i) => (
                    <m.div
                      key={pin.name}
                      className="absolute z-10"
                      style={{ left: `${pin.x}%`, top: `${pin.y}%`, translateX: '-50%', translateY: '-50%' }}
                      initial={reduce ? false : { scale: 0, opacity: 0 }}
                      animate={activeStep >= 1 ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 22, delay: reduce ? 0 : i * 0.14 }}
                    >
                      {activeStep === 1 && (
                        <m.span className="absolute rounded-full" style={{ inset: -7, background: 'hsl(var(--brand-lime))' }}
                          animate={{ scale: [1, 1.9, 1], opacity: [0.55, 0, 0.55] }}
                          transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
                        />
                      )}
                      <span className="relative z-10 flex h-[20px] w-[20px] items-center justify-center rounded-full border-2 border-white text-[8px] font-bold shadow-lg" style={{ background: 'hsl(var(--brand-lime))', color: '#0F1419' }}>
                        {i + 1}
                      </span>
                      {activeStep >= 2 && (
                        <m.span className="absolute left-6 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-lg px-2 py-[3px] text-[9px] font-semibold leading-none shadow-lg" style={{ background: '#0F1419', color: '#fff' }}
                          initial={reduce ? false : { opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.22 }}
                        >
                          {pin.name} · {pin.price}
                        </m.span>
                      )}
                    </m.div>
                  ))}
                  <m.div
                    className="absolute inset-x-0 bottom-0 z-20 rounded-t-2xl border-t bg-white p-3 shadow-2xl"
                    initial={reduce ? false : { y: '100%' }}
                    animate={activeStep >= 3 ? { y: 0 } : { y: '100%' }}
                    transition={{ type: 'spring', stiffness: 340, damping: 32 }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="relative h-[44px] w-[44px] shrink-0 overflow-hidden rounded-xl border bg-muted">
                        <Image src={firstPin.img} alt={firstPin.name} fill className="object-cover" sizes="44px" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[9px] font-semibold leading-tight text-gray-900">{firstPin.name}</p>
                        <p className="mt-0.5 text-[11px] font-bold text-gray-900">{firstPin.price}</p>
                      </div>
                    </div>
                    <m.button
                      className="mt-2 w-full rounded-xl py-[6px] text-[10px] font-bold text-white"
                      animate={{ background: activeStep >= 4 ? '#16a34a' : '#0F1419' }}
                      transition={{ duration: 0.28 }}
                    >
                      {activeStep >= 4 ? '✓ Added' : creative.cta}
                    </m.button>
                  </m.div>
                </div>
              </div>

              {/* Step list */}
              <div className="flex w-full flex-col gap-1 lg:max-w-[200px]">
                {STEPS.map((step, i) => (
                  <m.div key={step.label} className="flex items-start gap-2.5 rounded-xl px-3 py-2"
                    animate={{ background: activeStep === i ? 'rgba(200,255,82,0.10)' : 'rgba(0,0,0,0)' }}
                    transition={{ duration: 0.25 }}
                  >
                    <span className="mt-0.5 flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full text-[9px] font-bold transition-colors duration-300"
                      style={{ background: activeStep >= i ? 'hsl(var(--brand-lime))' : 'hsl(var(--muted))', color: '#0F1419' }}
                    >
                      {activeStep > i ? '✓' : i + 1}
                    </span>
                    <div>
                      <p className="text-xs font-semibold leading-tight">{step.label}</p>
                      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{step.sub}</p>
                    </div>
                  </m.div>
                ))}
                {done && !reduce && (
                  <button className="mt-2 self-start rounded-full border bg-secondary px-3 py-1 text-xs font-semibold transition hover:bg-muted"
                    onClick={() => setPlayKey((k) => k + 1)}
                  >
                    ↺ Replay
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* ── ON-SITE PDP VIEW: browser mockup ── */
          <div className="grid gap-0 lg:grid-cols-2">
            {/* Left: info card */}
            <div className="flex flex-col gap-5 border-b bg-[#0F1419] p-6 lg:border-b-0 lg:border-r lg:p-8">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-[#0F1419]" style={{ background: 'hsl(var(--brand-lime))' }}>
                  {industryIndex}
                </span>
                <div>
                  <h3 className="font-display text-xl font-semibold text-white">{creative.name}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-white/60">{creative.tagline}</p>
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/40">Shot list</p>
                <p className="text-sm leading-relaxed text-white/80">{creative.shotList}</p>
              </div>

              <div>
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/40">Tag these</p>
                <div className="flex flex-wrap gap-1.5">
                  {creative.tags.map((t) => (
                    <span key={t} className="rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-xs font-medium text-white">{t}</span>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-white/40">Idukki widget</p>
                <p className="text-sm font-medium text-white">{creative.widget}</p>
              </div>

              <div>
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/40">Embeds on</p>
                <div className="flex flex-wrap gap-1.5">
                  {creative.embeds.map((e) => (
                    <span key={e} className="rounded-full border border-white/15 bg-white/8 px-2 py-0.5 text-[11px] text-white/70">{e}</span>
                  ))}
                </div>
              </div>

              <div className="mt-auto grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-white/15 bg-white/8 px-4 py-3 text-center">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-white/40">CTA copy</p>
                  <p className="mt-1 text-base font-bold text-white">&ldquo;{creative.cta}&rdquo;</p>
                </div>
                <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/15 px-4 py-3 text-center">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-400/70">Expected lift</p>
                  <p className="mt-1 text-2xl font-bold text-emerald-400">{creative.liftNum}</p>
                  <p className="text-xs text-emerald-400/70">{creative.liftLabel}</p>
                </div>
              </div>
            </div>

            {/* Right: browser mockup with Shop the Look PDP */}
            <div className="flex items-center justify-center p-6 lg:p-8">
              <div className="w-full overflow-hidden rounded-xl border shadow-2xl" style={{ maxWidth: 520 }}>
                {/* Browser chrome */}
                <div className="flex items-center gap-2 border-b bg-[#f5f5f5] px-3 py-2.5">
                  <span className="flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
                  </span>
                  <div className="ml-2 flex-1 rounded-md bg-white px-3 py-1 text-[11px] text-gray-400 shadow-sm ring-1 ring-gray-200">
                    {creative.brand.toLowerCase().replace(/ /g, '')}.com / shop-the-look
                  </div>
                </div>

                {/* Shop the Look layout */}
                <div className="grid grid-cols-[1fr_auto] bg-white">
                  {/* Left: UGC image with pins */}
                  <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
                    <Image src={creative.hero} alt={`${creative.name} shop the look`} fill className="object-cover" sizes="260px" />
                    {/* "Shop this look" badge */}
                    <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 shadow-md backdrop-blur-sm">
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      <span className="text-[11px] font-semibold text-gray-900">Shop this look</span>
                    </div>
                    {/* Pins */}
                    {allPins.map((pin, i) => (
                      <div
                        key={pin.name}
                        className="absolute"
                        style={{ left: `${pin.x}%`, top: `${pin.y}%`, transform: 'translate(-50%,-50%)', zIndex: 10 }}
                      >
                        <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 border-white text-[9px] font-bold shadow-lg" style={{ background: 'hsl(var(--brand-lime))', color: '#0F1419' }}>
                          {i + 1}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Right: product list panel */}
                  <div className="flex w-[180px] shrink-0 flex-col border-l bg-white p-4">
                    <p className="mb-0.5 text-[9px] font-bold uppercase tracking-widest text-gray-400">Shop this look</p>
                    <p className="mb-4 text-sm font-bold text-gray-900">{creative.name}</p>
                    <div className="flex flex-1 flex-col gap-3 overflow-y-auto">
                      {allPins.map((pin) => (
                        <div key={pin.name} className="flex items-center gap-2.5">
                          <div className="relative h-[42px] w-[42px] shrink-0 overflow-hidden rounded-lg border bg-gray-50">
                            <Image src={pin.img} alt={pin.name} fill className="object-cover" sizes="42px" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[10px] font-semibold leading-tight text-gray-900">{pin.name}</p>
                            <p className="mt-0.5 text-[11px] font-bold text-gray-700">{pin.price}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button className="mt-4 w-full rounded-lg py-2 text-xs font-bold text-white" style={{ background: '#0F1419' }}>
                      {creative.cta}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LazyMotion>
  )
}
